import EndYokeOpeningScreen from "@dana/Components/FindEndYoke/EndYokeOpeningScreen";
import EndYokeResultScreen from "@dana/Components/FindEndYoke/EndYokeResultScreen";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import * as React from "react";
import { useEffect, useState } from "react";


const enum fields {
    auxiliaryTransmissionsImage = "auxiliaryTransmissionsImage",
    auxiliaryTransmissionsImageAltText = "auxiliaryTransmissionsImageAltText",

    singleAxlesImage = "singleAxlesImage",
    singleAxlesImageAltText = "singleAxlesImageAltText",

    splitShaftPtoImage = "splitShaftPtoImage",
    splitShaftPtoImageAltText = "splitShaftPtoImageAltText",

    tandemAxlesImage = "tandemAxlesImage",
    tandemAxlesImageAltText = "tandemAxlesImageAltText",

    transferCasesImage = "transferCasesImage",
    transferCasesImageAltText = "transferCasesImageAltText",

    transmissionsImage = "transmissionsImage",
    transmissionsImageAltText = "transmissionsImageAltText",

}

interface OwnProps extends PageProps {
    fields: {
        [fields.auxiliaryTransmissionsImage]: string;
        [fields.auxiliaryTransmissionsImageAltText]: string;
        [fields.singleAxlesImage]: string;
        [fields.singleAxlesImageAltText]: string;
        [fields.splitShaftPtoImage]: string;    
        [fields.splitShaftPtoImageAltText]: string;
        [fields.tandemAxlesImage]: string;
        [fields.tandemAxlesImageAltText]: string;
        [fields.transferCasesImage]: string;
        [fields.transferCasesImageAltText]: string;
        [fields.transmissionsImage]: string;
        [fields.transmissionsImageAltText]: string;
    };
}

export interface FindEndYokePageStyles {
    page?: MobiusPageProps;
}

export const findEndYokePageStyles: FindEndYokePageStyles = {
    page: {
        background: themeBackgrounds.offWhite,
        fullWidth: [true, true, true, true, true],
    },
}

const FindEndYokePage = ({ id, fields }: OwnProps) => {

    const styles = findEndYokePageStyles;

    const [pageNo, setPageNo] = useState<string | null>("1");
    const [assemblyObj, setAssemblyObj] = useState<string | null>(null);
    const [make, setMake] = useState<string | null>(null);
    const [model, setModel] = useState<string | null>(null);
    const [queryStringCollected, setQueryStringCollected] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const queryParams = new URLSearchParams(window.location.search);
            setPageNo(queryParams.get("page_no") ?? "1");
            setAssemblyObj(queryParams.get("assemblyObj"));
            setMake(queryParams.get("make"));
            setModel(queryParams.get("model"));
            setQueryStringCollected(true);
        }
    }, []);

    return (
        <Page {...styles.page}>
            {queryStringCollected && (!assemblyObj || !make || !model) && (
                <EndYokeOpeningScreen />
            )}

            {queryStringCollected && assemblyObj && make && model && (
                <EndYokeResultScreen
                    pageNo={pageNo ? Number(pageNo) : 1}
                    assembly={assemblyObj}
                    make={make}
                    model={model}
                    fields={fields}
                />
            )}
        </Page>
    )
};

const pageModule: PageModule = {
    component: FindEndYokePage,
    definition: {
        displayName: "Find End Yoke Page",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                fieldType: "Translatable",
                name: fields.auxiliaryTransmissionsImage,
                editorTemplate: "ImagePickerField",
                displayName: "Auxiliary Transmissions Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.auxiliaryTransmissionsImageAltText,
                editorTemplate: "TextField",
                displayName: "Auxiliary Transmissions Image Alt Text",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.singleAxlesImage,
                editorTemplate: "ImagePickerField",
                displayName: "Single Axles Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.singleAxlesImageAltText,
                editorTemplate: "TextField",
                displayName: "Single Axles Image Alt Text",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.splitShaftPtoImage,
                editorTemplate: "ImagePickerField",
                displayName: "Split Shaft PTO Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.splitShaftPtoImageAltText,
                editorTemplate: "TextField",
                displayName: "Split Shaft PTO Image Alt Text",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.tandemAxlesImage,
                editorTemplate: "ImagePickerField",
                displayName: "Tandem Axles Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.tandemAxlesImageAltText,
                editorTemplate: "TextField",
                displayName: "Tandem Axles Image Alt Text",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.transferCasesImage,
                editorTemplate: "ImagePickerField",
                displayName: "Transfer Cases Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.transferCasesImageAltText,
                editorTemplate: "TextField",
                displayName: "Transfer Cases Image Alt Text",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.transmissionsImage,
                editorTemplate: "ImagePickerField",
                displayName: "Transmissions Image",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.transmissionsImageAltText,
                editorTemplate: "TextField",
                displayName: "Transmissions Image Alt Text",
                defaultValue: "",
            },
        ],
    },
};

export default pageModule;
